import type { Incident } from "./types";

export type TimeOfDayBucket =
  | "morning"
  | "lunch"
  | "afternoon"
  | "after_school";

export const TIME_OF_DAY_LABELS: Record<TimeOfDayBucket, string> = {
  morning: "Morning (before 11 AM)",
  lunch: "Lunch period (11 AM – 1 PM)",
  afternoon: "Afternoon (1 – 3 PM)",
  after_school: "After school (after 3 PM)",
};

export const DAY_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export type CountEntry = {
  key: string;
  label: string;
  count: number;
  percentage: number;
};

export type StudentPattern = {
  studentKey: string;
  displayName: string;
  incidentCount: number;
  gradeLevel: string;
  repeatFlagCount: number;
  dominantDay: string | null;
  dominantDayCount: number;
  lastIncidentDate: string;
};

export type PatternInsight = {
  id: string;
  type: "time" | "day" | "student" | "student_day" | "repeat";
  title: string;
  description: string;
};

export type PatternAnalytics = {
  totalIncidents: number;
  hasEnoughData: boolean;
  timeOfDay: CountEntry[];
  dayOfWeek: CountEntry[];
  repeatStudents: StudentPattern[];
  insights: PatternInsight[];
  weeklyTrend: { weekLabel: string; count: number }[];
};

const MIN_INCIDENTS_FOR_PATTERNS = 3;
const PEAK_TIME_THRESHOLD = 0.35;
const PEAK_DAY_THRESHOLD = 0.3;
const REPEAT_STUDENT_MIN = 2;

export const normalizeStudentKey = (identifier: string): string =>
  identifier.trim().toLowerCase().replace(/\s+/g, " ");

export const getTimeOfDayBucket = (date: Date): TimeOfDayBucket => {
  const hour = date.getHours();
  if (hour < 11) return "morning";
  if (hour < 13) return "lunch";
  if (hour < 15) return "afternoon";
  return "after_school";
};

const buildWeeklyTrend = (incidents: Incident[]): { weekLabel: string; count: number }[] => {
  const weekCounts = new Map<string, number>();

  incidents.forEach((incident) => {
    const date = new Date(incident.created_at);
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day);
    startOfWeek.setHours(0, 0, 0, 0);
    const key = startOfWeek.toISOString().slice(0, 10);
    weekCounts.set(key, (weekCounts.get(key) ?? 0) + 1);
  });

  return [...weekCounts.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-8)
    .map(([weekStart, count]) => {
      const labelDate = new Date(weekStart);
      const month = labelDate.toLocaleString("en-US", { month: "short" });
      const day = labelDate.getDate();
      return { weekLabel: `${month} ${day}`, count };
    });
};

const buildStudentPatterns = (incidents: Incident[]): StudentPattern[] => {
  const byStudent = new Map<
    string,
    {
      displayName: string;
      incidents: Incident[];
      repeatFlagCount: number;
    }
  >();

  incidents.forEach((incident) => {
    const key = normalizeStudentKey(incident.student_identifier);
    if (!key) return;

    const existing = byStudent.get(key);
    if (existing) {
      existing.incidents.push(incident);
      if (incident.repeat_incident) existing.repeatFlagCount += 1;
    } else {
      byStudent.set(key, {
        displayName: incident.student_identifier.trim(),
        incidents: [incident],
        repeatFlagCount: incident.repeat_incident ? 1 : 0,
      });
    }
  });

  return [...byStudent.entries()]
    .filter(([, data]) => data.incidents.length >= REPEAT_STUDENT_MIN)
    .map(([studentKey, data]) => {
      const dayCounts = new Map<number, number>();
      data.incidents.forEach((incident) => {
        const day = new Date(incident.created_at).getDay();
        dayCounts.set(day, (dayCounts.get(day) ?? 0) + 1);
      });

      let dominantDay: number | null = null;
      let dominantDayCount = 0;
      dayCounts.forEach((count, day) => {
        if (count > dominantDayCount) {
          dominantDayCount = count;
          dominantDay = day;
        }
      });

      const sorted = [...data.incidents].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
      const latestGrade =
        sorted.find((i) => i.grade_level)?.grade_level ?? sorted[0]?.grade_level ?? "";

      return {
        studentKey,
        displayName: data.displayName,
        incidentCount: data.incidents.length,
        gradeLevel: latestGrade,
        repeatFlagCount: data.repeatFlagCount,
        dominantDay:
          dominantDay !== null && dominantDayCount >= 2
            ? DAY_LABELS[dominantDay]
            : null,
        dominantDayCount,
        lastIncidentDate: sorted[0]?.created_at ?? "",
      };
    })
    .sort((a, b) => b.incidentCount - a.incidentCount);
};

const buildInsights = (
  total: number,
  timeOfDay: CountEntry[],
  dayOfWeek: CountEntry[],
  repeatStudents: StudentPattern[],
): PatternInsight[] => {
  if (total < MIN_INCIDENTS_FOR_PATTERNS) return [];

  const insights: PatternInsight[] = [];

  const shortenTimeLabel = (label: string) =>
    label
      .replace(" (before 11 AM)", "")
      .replace(" (11 AM – 1 PM)", "")
      .replace(" (1 – 3 PM)", "")
      .replace(" (after 3 PM)", "");

  const peakTime = [...timeOfDay].sort((a, b) => b.count - a.count)[0];
  if (peakTime && peakTime.count > 0 && peakTime.percentage / 100 >= PEAK_TIME_THRESHOLD) {
    insights.push({
      id: `time-${peakTime.key}`,
      type: "time",
      title: `Incidents cluster around ${shortenTimeLabel(peakTime.label).toLowerCase()}`,
      description: `${peakTime.percentage}% of incidents (${peakTime.count} of ${total}) were logged during this window. Consider whether staffing, supervision, or transitions need adjustment during this period.`,
    });
  }

  const peakDay = [...dayOfWeek].sort((a, b) => b.count - a.count)[0];
  if (peakDay && peakDay.count > 0 && peakDay.percentage / 100 >= PEAK_DAY_THRESHOLD) {
    insights.push({
      id: `day-${peakDay.key}`,
      type: "day",
      title: `${peakDay.label}s stand out`,
      description: `${peakDay.count} incidents (${peakDay.percentage}%) occurred on ${peakDay.label}s. Review schedules, routines, or stressors that may be unique to this day.`,
    });
  }

  repeatStudents.slice(0, 3).forEach((student) => {
    if (student.dominantDay && student.dominantDayCount >= 2) {
      insights.push({
        id: `student-day-${student.studentKey}`,
        type: "student_day",
        title: `${student.displayName} often appears on ${student.dominantDay}s`,
        description: `${student.dominantDayCount} of ${student.incidentCount} incidents for this student were logged on ${student.dominantDay}. This may point to a schedule or environment factor worth exploring with the student and their team.`,
      });
    } else if (student.incidentCount >= 3) {
      insights.push({
        id: `student-${student.studentKey}`,
        type: "student",
        title: `${student.displayName} has ${student.incidentCount} incidents`,
        description: `This student appears repeatedly in incident records. A targeted support plan or check-in with their teacher may help address underlying needs before patterns escalate.`,
      });
    }
  });

  const flaggedRepeats = repeatStudents.filter((s) => s.repeatFlagCount >= 2);
  if (flaggedRepeats.length > 0) {
    insights.push({
      id: "repeat-flagged",
      type: "repeat",
      title: `${flaggedRepeats.length} student${flaggedRepeats.length === 1 ? "" : "s"} marked as repeat incidents`,
      description: `Multiple incidents were flagged as repeats during intake. These students may benefit from proactive outreach rather than waiting for the next referral.`,
    });
  }

  return insights;
};

export const computePatternAnalytics = (incidents: Incident[]): PatternAnalytics => {
  const withStudent = incidents.filter((i) => normalizeStudentKey(i.student_identifier));
  const totalIncidents = withStudent.length;

  const timeItems = withStudent.map((incident) => {
    const bucket = getTimeOfDayBucket(new Date(incident.created_at));
    return { key: bucket, label: TIME_OF_DAY_LABELS[bucket] };
  });

  const allTimeBuckets: CountEntry[] = (
    ["morning", "lunch", "afternoon", "after_school"] as TimeOfDayBucket[]
  ).map((bucket) => {
    const count = timeItems.filter((item) => item.key === bucket).length;
    return {
      key: bucket,
      label: TIME_OF_DAY_LABELS[bucket],
      count,
      percentage: totalIncidents > 0 ? Math.round((count / totalIncidents) * 100) : 0,
    };
  });

  const dayItems = withStudent.map((incident) => {
    const dayIndex = new Date(incident.created_at).getDay();
    return { key: String(dayIndex), label: DAY_LABELS[dayIndex] };
  });

  const allDays: CountEntry[] = DAY_LABELS.map((label, index) => {
    const count = dayItems.filter((item) => item.key === String(index)).length;
    return {
      key: String(index),
      label,
      count,
      percentage: totalIncidents > 0 ? Math.round((count / totalIncidents) * 100) : 0,
    };
  }).filter((entry) => entry.label !== "Sunday" && entry.label !== "Saturday" || entry.count > 0);

  // Keep Mon–Fri in calendar order for charts
  const dayOfWeek = DAY_LABELS.map((label, index) => {
    const entry = allDays.find((d) => d.key === String(index));
    return (
      entry ?? {
        key: String(index),
        label,
        count: 0,
        percentage: 0,
      }
    );
  }).filter((_, index) => index >= 1 && index <= 5);

  const repeatStudents = buildStudentPatterns(withStudent);
  const insights = buildInsights(totalIncidents, allTimeBuckets, dayOfWeek, repeatStudents);

  return {
    totalIncidents,
    hasEnoughData: totalIncidents >= MIN_INCIDENTS_FOR_PATTERNS,
    timeOfDay: allTimeBuckets,
    dayOfWeek,
    repeatStudents,
    insights,
    weeklyTrend: buildWeeklyTrend(withStudent),
  };
};
